import type { Academy } from '@court-sync/shared';
import type { LucideIcon } from 'lucide-react';
import { buildAcademyPath, getAcademyHostMode } from '@/lib/academyPaths';

type AcademyRouteFields = Pick<Academy, 'slug' | 'subdomain' | 'subdomainEnabled' | 'subscriptionTier'>;

function asAcademyFields(target: string | AcademyRouteFields): AcademyRouteFields {
  return typeof target === 'string' ? { slug: target } : target;
}
import {
  LayoutDashboard,
  MapPin,
  Users,
  Calendar,
  CalendarDays,
  BookOpen,
  ClipboardList,
  ClipboardCheck,
  CreditCard,
  GraduationCap,
  Search,
  Shield,
  Bell,
  Wallet,
  HeadphonesIcon,
  Tag,
  Mail,
  Award,
  UserCog,
  Clock,
  Layers,
  Tent,
  Webhook,
  Landmark,
  Megaphone,
  Flag,
  Package,
  Settings,
  IdCard,
  Trophy,
} from 'lucide-react';

export interface SidebarNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  tourId?: string;
  /** Shorter label for mobile bottom tab bar */
  mobileLabel?: string;
}

export type SidebarNavGroupId = 'facility' | 'academy' | 'club';

export type ConsumerSidebarNavGroupId = 'lessons' | 'club' | 'play';

export interface SidebarNavGroup {
  id: SidebarNavGroupId;
  label: string;
  items: SidebarNavItem[];
}

export interface ConsumerSidebarNavGroup {
  id: ConsumerSidebarNavGroupId;
  label: string;
  items: SidebarNavItem[];
}

export type UnifiedConsumerNavStructure =
  | { type: 'flat'; items: SidebarNavItem[] }
  | {
      type: 'grouped';
      leading: SidebarNavItem[];
      groups: ConsumerSidebarNavGroup[];
      trailing: SidebarNavItem[];
    };

export type UnifiedManagementNavStructure =
  | { type: 'flat'; items: SidebarNavItem[] }
  | {
      type: 'grouped';
      leading: SidebarNavItem[];
      groups: SidebarNavGroup[];
      trailing: SidebarNavItem[];
    };

const FACILITY_NAV_ROUTES = new Set(['/admin/calendar', '/admin/courts', '/admin/play-events']);
const PAYMENTS_NAV_ROUTE = '/admin/payments';

const DASHBOARD_NAV_ITEM: SidebarNavItem = {
  to: '/admin/dashboard',
  label: 'Dashboard',
  icon: LayoutDashboard,
};

const PAYMENTS_NAV_ITEM: SidebarNavItem = {
  to: PAYMENTS_NAV_ROUTE,
  label: 'Payments',
  icon: CreditCard,
  tourId: 'manage-payments',
  mobileLabel: 'Pay',
};

const ORGANIZATION_NAV_ITEM: SidebarNavItem = {
  to: '/admin/organization/settings',
  label: 'Organization',
  icon: Landmark,
  tourId: 'manage-organization',
  mobileLabel: 'Org',
};

const ACADEMY_SETTINGS_NAV_ITEM: SidebarNavItem = {
  to: '/admin/academy/settings',
  label: 'Academy settings',
  icon: Settings,
  tourId: 'manage-academy-settings',
  mobileLabel: 'Academy',
};

/** Academy owner nav — always includes Programs and Camps (no lesson-management mode split). */
export const academyOwnerNavItems: SidebarNavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/calendar', label: 'Calendar', icon: CalendarDays, tourId: 'manage-lessons' },
  { to: '/admin/courts', label: 'Courts', icon: MapPin, tourId: 'manage-courts' },
  { to: '/admin/staff', label: 'Coaches', icon: Users, tourId: 'manage-staff' },
  { to: '/admin/programs', label: 'Programs', icon: Layers, tourId: 'manage-programs' },
  { to: '/admin/terms', label: 'Terms', icon: Calendar, tourId: 'manage-terms' },
  { to: '/admin/camps', label: 'Camps', icon: Tent, tourId: 'manage-camps' },
  { to: '/admin/bookings', label: 'Bookings', icon: ClipboardList, tourId: 'manage-bookings' },
  { to: '/admin/payments', label: 'Payments', icon: CreditCard, tourId: 'manage-payments' },
  { to: '/admin/students', label: 'Students', icon: GraduationCap, tourId: 'manage-students' },
];

/** Same as owner except Payments (academy admins cannot manage payments). */
export const academyAdminNavItems: SidebarNavItem[] = academyOwnerNavItems.filter(
  (item) => item.to !== '/admin/payments'
);

/** Club operator nav — operational surfaces (payments live at org level in unified sidenav). */
export const clubManagementNavItems: SidebarNavItem[] = [
  { to: '/admin/court-hire', label: 'Court hire', icon: Clock, mobileLabel: 'Hire', tourId: 'club-nav-court-hire' },
  { to: '/admin/members', label: 'Members', icon: Users, tourId: 'club-nav-members' },
  { to: '/admin/inventory', label: 'Inventory', icon: Package, tourId: 'club-nav-inventory' },
];

/** @deprecated Club revenue uses the unified Payments hub at /admin/payments */
export const clubPaymentsNavItem: SidebarNavItem = {
  to: '/admin/payments',
  label: 'Club payments',
  icon: CreditCard,
  mobileLabel: 'Club pay',
};

/** @deprecated Use clubManagementNavItems via getUnifiedManagementNavItems */
export const clubOwnerNavItems: SidebarNavItem[] = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  ...clubManagementNavItems,
];

/** @deprecated Use getUnifiedManagementNavItems */
export const clubAdminNavItems: SidebarNavItem[] = clubManagementNavItems.filter(
  (item) => item.to !== '/admin/payments'
);

export const PLAY_NAV_ITEM: SidebarNavItem = {
  to: '/admin/play-events',
  label: 'Play events',
  icon: Trophy,
  tourId: 'manage-play',
  mobileLabel: 'Play events',
};

export interface UnifiedManagementNavOptions {
  hasAcademyManagement: boolean;
  hasClubModule: boolean;
  hasClubRole: boolean;
  /** User may operate club surfaces (club role or academy owner org authority). */
  canOperateClub: boolean;
  isAcademyOwner: boolean;
  isClubOwner: boolean;
  /** Organization has MATCH_PLAY entitlement (Professional+ or club module). */
  hasMatchPlay?: boolean;
}

function canManageOrganizationPayments(opts: UnifiedManagementNavOptions): boolean {
  return opts.isAcademyOwner || opts.isClubOwner;
}

function getFacilityNavItems(grouped: boolean, hasMatchPlay = false): SidebarNavItem[] {
  const items: SidebarNavItem[] = [
    {
      to: '/admin/calendar',
      label: 'Calendar',
      icon: CalendarDays,
      tourId: 'manage-lessons',
      mobileLabel: 'Calendar',
    },
    {
      to: '/admin/courts',
      label: grouped ? 'Courts & locations' : 'Courts',
      icon: MapPin,
      tourId: 'manage-courts',
      mobileLabel: grouped ? 'Courts' : undefined,
    },
  ];
  if (hasMatchPlay) {
    items.push(PLAY_NAV_ITEM);
  }
  return items;
}

function getAcademyManagementNavItems(
  opts: UnifiedManagementNavOptions,
  _grouped: boolean
): SidebarNavItem[] {
  if (!opts.hasAcademyManagement) return [];

  const items: SidebarNavItem[] = [];
  for (const item of academyOwnerNavItems) {
    if (item.to === '/dashboard') continue;
    if (FACILITY_NAV_ROUTES.has(item.to)) continue;
    if (item.to === PAYMENTS_NAV_ROUTE) continue;

    items.push(item);
  }
  items.push(ACADEMY_SETTINGS_NAV_ITEM);
  return items;
}

function getClubManagementNavItems(opts: UnifiedManagementNavOptions): SidebarNavItem[] {
  if (!opts.hasClubModule) return [];
  return [...clubManagementNavItems];
}

function shouldUseGroupedManagementNav(opts: UnifiedManagementNavOptions): boolean {
  const hasBothSurfaces = opts.hasAcademyManagement && opts.hasClubModule;
  const clubOnly = opts.hasClubModule && !opts.hasAcademyManagement;
  return hasBothSurfaces || clubOnly;
}

/**
 * Single sidenav for academy + club operators. Shared facility items and payments are
 * hoisted out of academy/club groups when multiple surfaces are visible.
 */
export function getUnifiedManagementNavStructure(
  opts: UnifiedManagementNavOptions
): UnifiedManagementNavStructure {
  if (shouldUseGroupedManagementNav(opts)) {
    const groups: SidebarNavGroup[] = [
      { id: 'facility', label: 'Facility', items: getFacilityNavItems(true, opts.hasMatchPlay) },
    ];

    const academyItems = getAcademyManagementNavItems(opts, true);
    if (academyItems.length > 0) {
      groups.push({ id: 'academy', label: 'Academy', items: academyItems });
    }

    const clubItems = getClubManagementNavItems(opts);
    if (clubItems.length > 0) {
      groups.push({ id: 'club', label: 'Club', items: clubItems });
    }

    const trailing: SidebarNavItem[] = [];
    if (canManageOrganizationPayments(opts)) {
      trailing.push(PAYMENTS_NAV_ITEM);
    }
    trailing.push(ORGANIZATION_NAV_ITEM);

    return {
      type: 'grouped',
      leading: [DASHBOARD_NAV_ITEM],
      groups,
      trailing,
    };
  }

  const flatItems: SidebarNavItem[] = [DASHBOARD_NAV_ITEM];

  if (opts.hasAcademyManagement) {
    for (const item of academyOwnerNavItems) {
      if (item.to === '/dashboard') continue;
      if (!opts.isAcademyOwner && item.to === PAYMENTS_NAV_ROUTE) continue;
      flatItems.push(item);
      if (item.to === '/admin/courts' && opts.hasMatchPlay) {
        flatItems.push(PLAY_NAV_ITEM);
      }
      if (item.to === '/admin/students') {
        flatItems.push(ACADEMY_SETTINGS_NAV_ITEM);
      }
    }
    flatItems.push(ORGANIZATION_NAV_ITEM);
  } else if (opts.hasClubModule) {
    flatItems.push(ORGANIZATION_NAV_ITEM);
  }

  return { type: 'flat', items: flatItems };
}

/**
 * Flat sidenav list for mobile tabs and other consumers that expect a single array.
 */
export function getUnifiedManagementNavItems(opts: UnifiedManagementNavOptions): SidebarNavItem[] {
  const structure = getUnifiedManagementNavStructure(opts);
  if (structure.type === 'flat') {
    return structure.items;
  }

  return [
    ...structure.leading,
    ...structure.groups.flatMap((group) => group.items),
    ...structure.trailing,
  ];
}

export function isOrganizationManagementRole(
  role: string
): role is 'academy_owner' | 'academy_admin' | 'club_owner' | 'club_admin' {
  return (
    role === 'academy_owner' ||
    role === 'academy_admin' ||
    role === 'club_owner' ||
    role === 'club_admin'
  );
}

export const studentNavItems: SidebarNavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/students/lessons', label: 'Browse Lessons', icon: Search, tourId: 'student-browse-lessons' },
  { to: '/students/calendar', label: 'My Calendar', icon: Calendar, tourId: 'student-calendar' },
  { to: '/students/bookings', label: 'My Bookings', icon: ClipboardList, tourId: 'student-bookings' },
  { to: '/students/credits', label: 'Make-up Credits', icon: Award, tourId: 'student-credits' },
  { to: '/students/my-students', label: 'My Students', icon: Users, tourId: 'student-my-students' },
];

export const adminNavItems: SidebarNavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/backoffice/risk', label: 'Risk', icon: Shield },
  { to: '/backoffice/notifications', label: 'Notifications', icon: Bell },
  { to: '/backoffice/payments', label: 'Payments', icon: CreditCard },
  { to: '/backoffice/webhook-events', label: 'Webhook events', icon: Webhook },
  { to: '/backoffice/accounting-sync', label: 'Accounting sync', icon: Landmark },
  { to: '/backoffice/payouts', label: 'Payouts', icon: Wallet },
  { to: '/backoffice/support', label: 'Support', icon: HeadphonesIcon },
  { to: '/backoffice/subscription-coupons', label: 'Subscription Coupons', icon: Tag },
  { to: '/backoffice/users', label: 'Users', icon: Users },
  { to: '/backoffice/campaigns', label: 'Campaigns', icon: Megaphone },
  { to: '/backoffice/feature-flags', label: 'Feature flags', icon: Flag },
  { to: '/backoffice/email-tester', label: 'Email Tester', icon: Mail },
];

export const coachNavItems: SidebarNavItem[] = [
  { to: '/coach/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/coach/schedule', label: 'My Schedule', icon: Calendar },
  { to: '/coach/availability', label: 'My Availability', icon: Clock },
  { to: '/coach/lessons', label: 'My Lessons', icon: BookOpen },
  { to: '/coach/roster', label: "Today's Roster", icon: ClipboardList },
  { to: '/coach/attendance', label: 'My Attendance', icon: ClipboardCheck },
  { to: '/coach/students', label: 'Students', icon: GraduationCap },
  { to: '/coach/profile', label: 'My Profile', icon: UserCog },
];

export function getNavItemsByRole(
  role: 'academy_owner' | 'academy_admin' | 'club_owner' | 'club_admin' | 'guardian' | 'admin' | 'coach'
): SidebarNavItem[] {
  switch (role) {
    case 'academy_owner':
      return academyOwnerNavItems;
    case 'academy_admin':
      return academyAdminNavItems;
    case 'club_owner':
    case 'club_admin':
      return getUnifiedManagementNavItems({
        hasAcademyManagement: false,
        hasClubModule: true,
        hasClubRole: true,
        canOperateClub: true,
        isAcademyOwner: false,
        isClubOwner: role === 'club_owner',
      });
    case 'guardian':
      return studentNavItems;
    case 'admin':
      return adminNavItems;
    case 'coach':
      return coachNavItems;
    default:
      return [];
  }
}

/** Guardian nav items scoped to academy routes (path or subdomain mode). */
export function getGuardianNavItemsWithAcademy(target: string | AcademyRouteFields): SidebarNavItem[] {
  const academy = asAcademyFields(target);
  const hostMode = getAcademyHostMode();
  const p = (subpath: string) => buildAcademyPath(academy, subpath, hostMode);
  const base = studentNavItems;
  return base.map((item) => {
    let to = item.to;
    if (to === '/dashboard') to = p('/dashboard');
    else if (to.startsWith('/students/')) to = p(`/${to.slice(10)}`);
    return { ...item, to };
  });
}

/** Coach nav items scoped to academy coach routes (path or subdomain mode). */
export function getCoachNavItemsWithAcademy(target: string | AcademyRouteFields): SidebarNavItem[] {
  const academy = asAcademyFields(target);
  const hostMode = getAcademyHostMode();
  const p = (subpath: string) => buildAcademyPath(academy, subpath, hostMode);
  return coachNavItems.map((item) => ({
    ...item,
    to: p(`/coach${item.to.replace('/coach', '')}`),
  }));
}

/** Base consumer lesson nav routes (org-relative subpaths). */
export const consumerLessonsNavItems: SidebarNavItem[] = [
  { to: '/lessons', label: 'Browse Lessons', icon: Search, tourId: 'student-browse-lessons' },
  { to: '/calendar', label: 'My Calendar', icon: Calendar, tourId: 'student-calendar' },
  { to: '/bookings', label: 'My Bookings', icon: ClipboardList, tourId: 'student-bookings' },
  { to: '/credits', label: 'Make-up Credits', icon: Award, tourId: 'student-credits' },
  { to: '/my-students', label: 'My Students', icon: Users, tourId: 'student-my-students' },
];

/** Base consumer club member nav routes (org-relative subpaths). */
export const consumerClubMemberNavItems: SidebarNavItem[] = [
  { to: '/membership', label: 'Membership', icon: IdCard, tourId: 'member-membership' },
  { to: '/notices', label: 'Notices', icon: Megaphone, tourId: 'member-notices' },
];

const CONSUMER_DASHBOARD_NAV_ITEM: SidebarNavItem = {
  to: '/dashboard',
  label: 'Dashboard',
  icon: LayoutDashboard,
};

const CONSUMER_NOTIFICATIONS_NAV_ITEM: SidebarNavItem = {
  to: '/notifications',
  label: 'Notifications',
  icon: Bell,
  mobileLabel: 'Alerts',
};

/** Base consumer play nav routes (org-relative subpaths). */
export const consumerPlayNavItems: SidebarNavItem[] = [
  { to: '/play', label: 'Play events', icon: Trophy, tourId: 'member-play-events' },
  { to: '/play/my-matches', label: 'My matches', icon: Trophy, tourId: 'member-my-matches' },
];

export interface UnifiedConsumerNavOptions {
  hasLessonsAccess: boolean;
  hasClubMemberAccess: boolean;
  hasMemberships: boolean;
  hasCourtHire: boolean;
  hasInventoryHire: boolean;
  hasMatchPlay?: boolean;
  /** Resolve org-relative subpath to full route (e.g. `/club-academy/:slug/dashboard`). */
  pathFor: (subpath: string) => string;
}

function scopeConsumerNavItems(items: SidebarNavItem[], pathFor: (subpath: string) => string): SidebarNavItem[] {
  return items.map((item) => ({ ...item, to: pathFor(item.to) }));
}

function getConsumerLessonsNavItems(opts: UnifiedConsumerNavOptions): SidebarNavItem[] {
  if (!opts.hasLessonsAccess) return [];
  return scopeConsumerNavItems(consumerLessonsNavItems, opts.pathFor);
}

function getConsumerClubNavItems(opts: UnifiedConsumerNavOptions): SidebarNavItem[] {
  if (!opts.hasClubMemberAccess) return [];

  const items: SidebarNavItem[] = [];
  if (opts.hasMemberships) {
    items.push(consumerClubMemberNavItems[0]);
  }
  items.push(consumerClubMemberNavItems[1]);
  return scopeConsumerNavItems(items, opts.pathFor);
}

function getConsumerPlayNavItems(opts: UnifiedConsumerNavOptions): SidebarNavItem[] {
  if (!opts.hasMatchPlay) return [];
  return scopeConsumerNavItems(consumerPlayNavItems, opts.pathFor);
}

function shouldUseGroupedConsumerNav(opts: UnifiedConsumerNavOptions): boolean {
  const surfaces =
    Number(Boolean(opts.hasLessonsAccess)) +
    Number(Boolean(opts.hasClubMemberAccess)) +
    Number(Boolean(opts.hasMatchPlay));
  return surfaces >= 2;
}

export function getUnifiedConsumerNavStructure(opts: UnifiedConsumerNavOptions): UnifiedConsumerNavStructure {
  const dashboardItem = { ...CONSUMER_DASHBOARD_NAV_ITEM, to: opts.pathFor('/dashboard') };
  const trailing = [{ ...CONSUMER_NOTIFICATIONS_NAV_ITEM, to: opts.pathFor('/notifications') }];

  if (shouldUseGroupedConsumerNav(opts)) {
    const groups: ConsumerSidebarNavGroup[] = [];
    const lessonsItems = getConsumerLessonsNavItems(opts);
    if (lessonsItems.length > 0) {
      groups.push({ id: 'lessons', label: 'Lessons', items: lessonsItems });
    }
    const clubItems = getConsumerClubNavItems(opts);
    if (clubItems.length > 0) {
      groups.push({ id: 'club', label: 'Club', items: clubItems });
    }
    const playItems = getConsumerPlayNavItems(opts);
    if (playItems.length > 0) {
      groups.push({ id: 'play', label: 'Play events', items: playItems });
    }
    return { type: 'grouped', leading: [dashboardItem], groups, trailing };
  }

  const flatItems: SidebarNavItem[] = [dashboardItem];
  flatItems.push(...getConsumerLessonsNavItems(opts));
  flatItems.push(...getConsumerClubNavItems(opts));
  flatItems.push(...getConsumerPlayNavItems(opts));
  flatItems.push(...trailing);
  return { type: 'flat', items: flatItems };
}

export function getUnifiedConsumerNavItems(opts: UnifiedConsumerNavOptions): SidebarNavItem[] {
  const structure = getUnifiedConsumerNavStructure(opts);
  if (structure.type === 'flat') {
    return structure.items;
  }
  return [
    ...structure.leading,
    ...structure.groups.flatMap((group) => group.items),
    ...structure.trailing,
  ];
}

/** Org-relative subpaths omitted from the public top nav (covered by primary links or profile menu). */
const PUBLIC_ORG_LESSONS_DROPDOWN_EXCLUDE = new Set(['/lessons']);

/** Academy / lessons portal items for the signed-in public org top nav dropdown. */
export function getPublicOrgLessonsDropdownItems(opts: UnifiedConsumerNavOptions): SidebarNavItem[] {
  if (!opts.hasLessonsAccess) return [];
  return scopeConsumerNavItems(
    consumerLessonsNavItems.filter((item) => !PUBLIC_ORG_LESSONS_DROPDOWN_EXCLUDE.has(item.to)),
    opts.pathFor
  );
}
